<script setup lang="ts">
import { computed, onMounted, PropType, ref } from 'vue';
import { Trip } from "@/models/Trip";
import { SavedOffer } from "@/models/SavedOffer";
import { getSavedOffers } from "@/services/trip.service";
import TripDeleteModal from "@/components/TripCard/TripDeleteModal.vue";
import TripEditModal from "@/components/TripCard/TripEditModal.vue";
import { useI18n } from "vue-i18n";
import {useTripsStore} from "@/stores/tripsStore";

const props = defineProps({
  trip: {
    type: Object as PropType<Trip>,
    required: true
  },
});


const tripsStore = useTripsStore();
const { t } = useI18n(); // Use the i18n instance

const colors = ['#faffd8', '#fff3eb', '#eafeec', '#efebff'];
const color = colors[Math.floor(Math.random() * colors.length)];
const backgroundImageClass = computed(() => ({ backgroundImage: `url('${props.trip.backgroundImageUrl}')` }));

const statusStyle = computed(() => {

  if(tripsStore.isStatusActive(props.trip) || tripsStore.isStatusCompleted(props.trip)){
    return { background: '#9be7b9' };
  }

  if(tripsStore.isStatusCancelled(props.trip)){
      return { background: '#ff9d9d' };
  }

  if(tripsStore.isStatusPlanned(props.trip)){
      return { background: '#f9f9f9' };
  }

  return { background: 'black' };
});

const statusText = computed(() => {

  if(tripsStore.isStatusActive(props.trip)){
    return t('tripCard.confirmed');
  }

  if(tripsStore.isStatusCompleted(props.trip)){
    return t('tripCard.completed');
  }

  if(tripsStore.isStatusCancelled(props.trip)){
      return t('tripCard.cancelled');
  }

  if(tripsStore.isStatusPlanned(props.trip)){
      return t('tripCard.planned');
  }

  return t('tripCard.unknown');

});

const showDeleteModal = ref(false);
const showEditModal = ref(false);
const offer = ref<SavedOffer[]>([]);

onMounted(() =>
  getSavedOffers(props.trip.uuid).then(data => {
    offer.value = data.data;
  })
);
</script>

<template>
  <div class="flex flex-col rounded-11xl" :style="{ background: color }" v-if="offer !== null">
    <div :style="backgroundImageClass"
      class="flex flex-col justify-space-between bg-cover bg-no-repeat h-56 rounded-11xl cursor-pointer p-5"
      @click="$router.push(`/trips/${trip.uuid}`)">

      <div class="responsive-body-xxsmall rounded-11xl flex px-3 py-1 w-fit flex-row gap-1.5 justify-center" :style="statusStyle">
        <img v-if="tripsStore.isStatusActive(trip) || tripsStore.isStatusPlanned(trip)" src="@/assets/icons/svg/Confirmed_16x16.svg"
          alt="" width="16" height="16" />
        <img v-else-if="tripsStore.isStatusCompleted(trip)" src="@/assets/icons/svg/Completed_16x16.svg" alt="" width="16"
          height="16" />
        <img v-else-if="tripsStore.isStatusCancelled(trip)" src="@/assets/icons/svg/Cancelled_16x16.svg" alt="" width="16"
          height="16" />
        <span style="line-height:17px">{{ statusText }}</span>
      </div>
      <div class="responsive-title-5 font-semibold text-white trip-name">{{ trip.name }}</div>
    </div>
    <div class="flex flex-row p-4 justify-between items-center">
      <div class="flex  responsive-body-medium saved-offer__text gap-x-1.5">
        <span class="font-semibold">{{ props.trip.offers.length }}</span> {{ t('tripCard.savedOffers') }}
      </div>
      <ElDropdown trigger="click" placement="bottom">
        <img src="@/assets/icons/svg/Menu dots_24x24.svg">
        <template #dropdown>
          <ElDropdownMenu>
            <ElDropdownItem class="text-black flex gap-2" @click="showEditModal = true">
              <img src="@/assets/icons/svg/Edit_24x24.svg" />
              {{ t('tripCard.renameTrip') }}
            </ElDropdownItem>
            <ElDropdownItem class="text-black flex gap-2" @click="showDeleteModal = true">
              <img src="@/assets/icons/svg/Delete_24x24.svg" />
              {{ t('tripCard.deleteTrip') }}
            </ElDropdownItem>
          </ElDropdownMenu>
        </template>
      </ElDropdown>
    </div>
  </div>
  <TripDeleteModal :showDeleteModal="showDeleteModal" @on-close="showDeleteModal = false" :trip="trip" @onDeleteClick="$emit('onDeleteClick', $event)" />
  <TripEditModal :show="showEditModal" :name="trip.name" @onClose="showEditModal = false"
    @onEditClick="showEditModal = false; $emit('onEditClick', { name: $event, id: trip.uuid })" />
</template>


<style scoped>
@media (max-width: 1024px) {
  .trip-name {
    font-size: 1.25rem;
  }

  .saved-offer__text {
    font-size: .875rem;
  }
}
</style>
